<template>
	<v-row class="mb-3">
		<v-col
			v-for="(item, index) in displayItems"
			:key="index"
			:cols="getCols(items.length)"
			:sm="getSm(items.length)"
			:md="getMd(items.length)"
		>
			<div class="kpi-card" :style="cardStyle(item, index)">
				<div class="kpi-card__icon-ring" :style="iconRingStyle(item)">
					<v-icon :color="item.color" size="22">
						{{ item.icon }}
					</v-icon>
				</div>
				<div class="kpi-card__info">
					<span class="kpi-card__value" :style="valueStyle(item)">
						{{ formatValue(item.value) }}
					</span>
					<span class="kpi-card__label">
						{{ item.label }}
					</span>
				</div>
				<div class="kpi-card__accent" :style="accentStyle(item)" />
			</div>
		</v-col>
	</v-row>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export interface BigNumberItem {
	label: string;
	value: number | string;
	icon: string;
	color?: string;
}

interface Props {
	items: BigNumberItem[];
}

const props = defineProps<Props>();

const { locale } = useI18n();

const displayItems = computed(() => {
	return props.items.slice(0, 4);
});

const getCols = (itemCount: number): number => {
	if (itemCount <= 3) return 12;
	return 12;
};

const getSm = (itemCount: number): number => {
	if (itemCount === 1) return 12;
	if (itemCount === 2) return 6;
	if (itemCount === 3) return 4;
	return 6;
};

const getMd = (itemCount: number): number => {
	if (itemCount === 1) return 12;
	if (itemCount === 2) return 6;
	if (itemCount === 3) return 4;
	return 3;
};

/*
	O idioma vem do app (`locale` do vue-i18n), não do navegador. Sem o argumento,
	`toLocaleString()` usa a língua DO NAVEGADOR: com o app em português e o
	Windows em inglês, 1234 saía "1,234" em vez de "1.234".
*/
const formatValue = (value: number | string): string => {
	if (typeof value === "number") {
		return value.toLocaleString(locale.value);
	}
	return String(value);
};

const cardStyle = (_item: BigNumberItem, index: number) => ({
	animationDelay: `${index * 0.1}s`,
});

const iconRingStyle = (item: BigNumberItem) => {
	const color = item.color || "primary";
	if (color.includes("#")) {
		return {
			background: `${color}14`,
		};
	}
	return {};
};

const valueStyle = (item: BigNumberItem) => {
	const color = item.color || "primary";
	if (color.includes("#")) {
		return { color };
	}
	return {};
};

const accentStyle = (item: BigNumberItem) => {
	const color = item.color || "primary";
	if (color.includes("#")) {
		return {
			background: `linear-gradient(to right, ${color}, transparent)`,
		};
	}
	return {};
};
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;

.kpi-card {
	position: relative;
	display: flex;
	align-items: center;
	gap: 1rem;
	padding: 1rem 1.25rem;
	border-radius: 12px;
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-theme-primary), 0.08);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	overflow: hidden;
	transition: all 0.3s ease;
	animation: kpiEnter 0.5s ease-out both;

	&:hover {
		transform: translateY(-3px);
		box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.1);
	}

	&__icon-ring {
		flex-shrink: 0;
		width: 44px;
		height: 44px;
		border-radius: 50%;
		background: rgba(var(--v-theme-primary), 0.08);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__info {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		min-width: 0;
		flex: 1;
	}

	&__value {
		font-family: vars.$font-heading;
		font-size: 1.5rem;
		font-weight: 700;
		line-height: 1.2;
		color: rgb(var(--v-theme-primary));
	}

	&__label {
		font-family: vars.$font-secondary;
		font-size: 0.8rem;
		color: rgb(var(--v-theme-on-surface));
		opacity: 0.6;
		font-weight: 500;
		letter-spacing: 0.02em;
	}

	// Bottom accent line
	&__accent {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: linear-gradient(to right, rgb(var(--v-theme-primary)), transparent);
		opacity: 0.5;
	}
}

@keyframes kpiEnter {
	from {
		opacity: 0;
		transform: translateY(12px) scale(0.97);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}
</style>
